import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import {
  AlertOctagon,
  TrendingUp,
  ShieldAlert,
  Zap,
  Unlock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';

const ICONS_BY_TYPE = {
  monto_escalando: TrendingUp,
  alertas_max_historico: AlertTriangle,
  nueva_alerta_severa: ShieldAlert,
  reactivacion_bloqueada: Unlock,
  velocidad_aumentando: Zap,
};

const SEVERITY_STYLES = {
  alta: {
    container: 'bg-gradient-to-r from-red-50 to-rose-50 border-red-400',
    pill: 'bg-red-600 text-white',
    icon: 'text-red-600',
    title: 'text-red-800',
    borderLeft: 'border-l-4 border-l-red-500',
  },
  media: {
    container: 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-400',
    pill: 'bg-amber-500 text-white',
    icon: 'text-amber-600',
    title: 'text-amber-800',
    borderLeft: 'border-l-4 border-l-amber-500',
  },
  baja: {
    container: 'bg-yellow-50 border-yellow-300',
    pill: 'bg-yellow-500 text-white',
    icon: 'text-yellow-600',
    title: 'text-yellow-800',
    borderLeft: 'border-l-4 border-l-yellow-400',
  },
};

const SEVERITY_LABEL = { alta: 'Alto', media: 'Medio', baja: 'Bajo' };

export default function EscalationBanner({ caseId }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!caseId) return;
    let cancel = false;
    (async () => {
      try {
        const res = await api.get(`/cases/${caseId}/escalation-signals`);
        if (!cancel) setData(res.data);
      } catch {
        if (!cancel) setData(null);
      }
    })();
    return () => { cancel = true; };
  }, [caseId]);

  if (!data || !data.has_escalation || dismissed) return null;

  const style = SEVERITY_STYLES[data.severity] || SEVERITY_STYLES.baja;
  const signals = data.signals || [];

  return (
    <div
      className={`mb-4 rounded-lg border ${style.container} ${style.borderLeft} shadow-sm overflow-hidden`}
      data-testid="escalation-banner"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <AlertOctagon size={22} className={`${style.icon} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-bold ${style.title}`}>
              Comportamiento escalando
            </span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${style.pill}`}>
              Nivel {SEVERITY_LABEL[data.severity] || data.severity}
            </span>
            <span className="text-xs text-slate-600">
              {signals.length} señal{signals.length !== 1 ? 'es' : ''} detectada{signals.length !== 1 ? 's' : ''} vs histórico del cliente
            </span>
          </div>

          {/* Resumen bullets de primeras 2 señales siempre visible */}
          {!expanded && signals.length > 0 && (
            <ul className="mt-2 space-y-1">
              {signals.slice(0, 2).map((s, i) => {
                const Icon = ICONS_BY_TYPE[s.tipo] || AlertTriangle;
                return (
                  <li key={i} className="flex items-start gap-2 text-[12px] text-slate-700">
                    <Icon size={12} className={`${style.icon} flex-shrink-0 mt-0.5`} />
                    <span>{s.titulo}</span>
                  </li>
                );
              })}
              {signals.length > 2 && (
                <li className="text-[11px] text-slate-500 italic pl-5">+ {signals.length - 2} señal{signals.length - 2 !== 1 ? 'es' : ''} más</li>
              )}
            </ul>
          )}

          {/* Vista expandida: todas las señales con descripción */}
          {expanded && (
            <div className="mt-3 space-y-2">
              {signals.map((s, i) => {
                const Icon = ICONS_BY_TYPE[s.tipo] || AlertTriangle;
                return (
                  <div key={i} className="bg-white/70 border border-slate-200 rounded-md px-3 py-2">
                    <div className="flex items-start gap-2">
                      <Icon size={14} className={`${style.icon} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1">
                        <div className={`font-semibold text-[12px] ${style.title}`}>{s.titulo}</div>
                        <div className="text-[11px] text-slate-600 mt-0.5">{s.descripcion}</div>
                        {s.metrica && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {Object.entries(s.metrica).map(([k, v]) => (
                              <span key={k} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">
                                <span className="text-slate-400">{k.replace(/_/g, ' ')}:</span>{' '}
                                <strong className="text-slate-800">
                                  {typeof v === 'number'
                                    ? (v > 1000 ? v.toLocaleString('es-AR', { maximumFractionDigits: 0 }) : v)
                                    : Array.isArray(v) ? v.join(', ') : String(v)}
                                </strong>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Resumen histórico */}
              {data.historico_resumen && (
                <div className="text-[11px] text-slate-500 pt-1 border-t border-slate-200">
                  <strong className="text-slate-700">Referencia histórica:</strong>{' '}
                  {data.historico_resumen.count_casos} caso{data.historico_resumen.count_casos !== 1 ? 's' : ''} anterior{data.historico_resumen.count_casos !== 1 ? 'es' : ''} · Promedio{' '}
                  {data.historico_resumen.promedio_monto > 0 && (
                    <>${Math.round(data.historico_resumen.promedio_monto).toLocaleString('es-AR')} · </>
                  )}
                  {Math.round(data.historico_resumen.promedio_alertas * 10) / 10} alertas/caso · Máx {data.historico_resumen.max_alertas} alertas
                </div>
              )}
            </div>
          )}

          {/* Toggle */}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 hover:text-slate-800"
            data-testid="escalation-toggle"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? 'Ocultar detalle' : 'Ver detalle completo'}
          </button>
        </div>

        <button
          onClick={() => setDismissed(true)}
          className="text-slate-400 hover:text-slate-600 transition-colors"
          title="Ocultar este banner en esta sesión"
          data-testid="escalation-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
